'use client';
import { useMemo } from 'react';
import { useAppStore } from '@/stores/app-store';

/*
 * System Overview — สรุป "โครงสร้าง + feature + แผนพัฒนา" ของ MomoSpace 4.0
 * ในหน้าเดียว (สำหรับทีม/ลูกค้า/นักลงทุน). หน้านี้เป็น static reference —
 * แก้ข้อมูลได้ที่ 3 ก้อนด้านล่าง: ARCH (โครงสร้าง) · FEATURES · PHASES (แผนพัฒนา).
 */

type L = { th: string; en: string };
const tx = (v: L, lang: string) => (lang === 'th' ? v.th : v.en);

type FStatus = 'done' | 'doing' | 'planned' | 'future';
const SM: Record<FStatus, { label: L; color: string }> = {
  done: { label: { th: 'เสร็จแล้ว', en: 'Done' }, color: 'var(--green)' },
  doing: { label: { th: 'กำลังทำ', en: 'In progress' }, color: 'var(--blue)' },
  planned: { label: { th: 'วางแผน', en: 'Planned' }, color: 'var(--amber)' },
  future: { label: { th: 'อนาคต', en: 'Future' }, color: 'var(--purple)' },
};

/* ────────── 1) โครงสร้างระบบ (Architecture) ────────── */
interface ArchLayer { icon: string; color: string; title: L; items: L[] }
const ARCH: ArchLayer[] = [
  {
    icon: 'layout', color: 'var(--blue)', title: { th: 'Frontend', en: 'Frontend' },
    items: [
      { th: 'Next.js 16 (App Router) + React 19', en: 'Next.js 16 (App Router) + React 19' },
      { th: 'TypeScript (strict) · Zustand (single store)', en: 'TypeScript (strict) · Zustand (single store)' },
      { th: 'Tailwind CSS 4 · ธีม light/dark', en: 'Tailwind CSS 4 · light/dark theme' },
      { th: 'ApexCharts · Leaflet · react-grid-layout', en: 'ApexCharts · Leaflet · react-grid-layout' },
    ],
  },
  {
    icon: 'server', color: 'var(--green)', title: { th: 'Backend / API', en: 'Backend / API' },
    items: [
      { th: 'Next.js Route Handlers (app/api)', en: 'Next.js Route Handlers (app/api)' },
      { th: '17+ endpoints · SQL แบบ parameterized', en: '17+ endpoints · parameterized SQL' },
      { th: 'mysql2 connection pool (10 conn)', en: 'mysql2 connection pool (10 conn)' },
      { th: 'JWT session lifecycle (login/me/logout)', en: 'JWT session lifecycle (login/me/logout)' },
    ],
  },
  {
    icon: 'database', color: 'var(--amber)', title: { th: 'ฐานข้อมูล', en: 'Database' },
    items: [
      { th: '2 pools บน MySQL เดียวกัน', en: 'Two pools on one MySQL server' },
      { th: 'momo_momodb — user/auth/device + telemetry', en: 'momo_momodb — users/auth/devices + telemetry' },
      { th: 'momo_connect — project/billing/PM/notify', en: 'momo_connect — projects/billing/PM/notify' },
      { th: 'ตาราง usr_<mac_id> 6,800+ ตาราง', en: 'usr_<mac_id> per-device tables (6,800+)' },
    ],
  },
  {
    icon: 'shield', color: 'var(--purple)', title: { th: 'Auth & สิทธิ์', en: 'Auth & Access' },
    items: [
      { th: 'JWT + HTTP-only cookie', en: 'JWT + HTTP-only cookie' },
      { th: '4 บทบาท: admin/manager/operator/viewer', en: '4 roles: admin/manager/operator/viewer' },
      { th: 'ACL 2 ชั้น: ระดับอุปกรณ์ + ระดับ project', en: 'Two-tier ACL: device-level + project-level' },
      { th: 'รองรับ bcrypt + legacy PHP hash', en: 'Supports bcrypt + legacy PHP hashes' },
    ],
  },
  {
    icon: 'layers', color: 'var(--cyan)', title: { th: 'Multi-tenant', en: 'Multi-tenant' },
    items: [
      { th: 'Site Manager — แยก subdomain/branding', en: 'Site Manager — per-subdomain branding' },
      { th: 'ซ่อน/แสดงเมนูต่อไซต์ (site menu)', en: 'Per-site menu visibility' },
      { th: 'Entitlements / add-on ต่อไซต์', en: 'Entitlements / add-ons per site' },
      { th: 'plant.momoagentix.com = ไซต์ default', en: 'plant.momoagentix.com = default site' },
    ],
  },
  {
    icon: 'data', color: 'var(--red)', title: { th: 'Data Model', en: 'Data Model' },
    items: [
      { th: 'per-device dynamic table (data01..50)', en: 'per-device dynamic tables (data01..50)' },
      { th: 'map ชื่อ field ผ่าน tbl_config_data', en: 'field-name mapping via tbl_config_data' },
      { th: 'energy_usage_day / month / year', en: 'energy_usage_day / month / year' },
      { th: 'widget เก็บเป็น JSON (ไม่ต้อง migrate)', en: 'widgets stored as JSON (no migration)' },
    ],
  },
];

/* ────────── 2) Features ────────── */
interface Feat { name: L; status: FStatus }
interface FeatGroup { title: L; color: string; items: Feat[] }
const FEATURES: FeatGroup[] = [
  {
    title: { th: 'มอนิเตอร์ & อุปกรณ์', en: 'Monitoring & Devices' }, color: 'var(--blue)',
    items: [
      { name: { th: 'Plants / Devices / Telemetry', en: 'Plants / Devices / Telemetry' }, status: 'done' },
      { name: { th: 'Plant Overview (รวมตามหมวด)', en: 'Plant Overview (by category)' }, status: 'done' },
      { name: { th: 'Monitor (กราฟ + event log)', en: 'Monitor (chart + event log)' }, status: 'done' },
      { name: { th: 'Energy Dashboard (kW / kWh + ค่าไฟ)', en: 'Energy Dashboard (kW / kWh + cost)' }, status: 'done' },
      { name: { th: 'รองรับ 8 ประเภทอุปกรณ์', en: '8 device types supported' }, status: 'done' },
    ],
  },
  {
    title: { th: 'Dashboard & Visualization', en: 'Dashboard & Visualization' }, color: 'var(--purple)',
    items: [
      { name: { th: 'Custom Widgets (24+ ชนิด · drag-resize)', en: 'Custom Widgets (24+ types · drag-resize)' }, status: 'doing' },
      { name: { th: 'WWTP SCADA Widget (M-TECH)', en: 'WWTP SCADA Widget (M-TECH)' }, status: 'done' },
      { name: { th: 'Plant Location Map (Leaflet + geocode)', en: 'Plant Location Map (Leaflet + geocode)' }, status: 'doing' },
      { name: { th: 'หน้า Energy / Env / Solar / Water', en: 'Energy / Env / Solar / Water pages' }, status: 'done' },
    ],
  },
  {
    title: { th: 'ปฏิบัติการ & AI', en: 'Operations & AI' }, color: 'var(--green)',
    items: [
      { name: { th: 'AI Insights / Anomaly (rule-based)', en: 'AI Insights / Anomaly (rule-based)' }, status: 'done' },
      { name: { th: 'Preventive Maintenance (แผน/ปฏิทิน/ประวัติ)', en: 'Preventive Maintenance (plan/calendar/history)' }, status: 'doing' },
      { name: { th: 'แจ้งเตือน LINE / Telegram / Email', en: 'Notifications: LINE / Telegram / Email' }, status: 'doing' },
    ],
  },
  {
    title: { th: 'แพลตฟอร์ม & Admin', en: 'Platform & Admin' }, color: 'var(--amber)',
    items: [
      { name: { th: 'User & Role Permissions (RBAC)', en: 'User & Role Permissions (RBAC)' }, status: 'doing' },
      { name: { th: 'Site Manager (multi-tenant)', en: 'Site Manager (multi-tenant)' }, status: 'done' },
      { name: { th: 'License & Billing / Quotas', en: 'License & Billing / Quotas' }, status: 'planned' },
      { name: { th: 'i18n (EN/TH) + ธีม light/dark', en: 'i18n (EN/TH) + light/dark theme' }, status: 'done' },
    ],
  },
];

/* ────────── 3) แผนพัฒนา (Roadmap phases) ────────── */
interface Phase { title: L; period: string; status: FStatus; items: L[] }
const PHASES: Phase[] = [
  {
    title: { th: 'แกนระบบ & รากฐาน', en: 'Core & Foundation' }, period: 'H1 2026', status: 'done',
    items: [
      { th: 'Next.js/React/TS · Zustand · ธีม · i18n · demo mode', en: 'Next.js/React/TS · Zustand · theme · i18n · demo mode' },
    ],
  },
  {
    title: { th: 'ข้อมูล & อุปกรณ์', en: 'Data & Devices' }, period: 'H1 2026', status: 'done',
    items: [
      { th: 'JWT auth · MySQL pools · telemetry ต่ออุปกรณ์ · CRUD · 17 API', en: 'JWT auth · MySQL pools · per-device telemetry · CRUD · 17 APIs' },
    ],
  },
  {
    title: { th: 'Dashboard & AI', en: 'Dashboards & AI' }, period: 'Q2 2026', status: 'done',
    items: [
      { th: 'Custom widgets · ApexCharts · Leaflet · SCADA · rule-based AI', en: 'Custom widgets · ApexCharts · Leaflet · SCADA · rule-based AI' },
    ],
  },
  {
    title: { th: 'รอบพัฒนาปัจจุบัน', en: 'Current Build' }, period: 'Q3 2026', status: 'doing',
    items: [
      { th: 'RBAC · Location Map · PM · Telegram notify · Site Manager · ปรับ widget', en: 'RBAC · Location Map · PM · Telegram notify · Site Manager · widget polish' },
    ],
  },
  {
    title: { th: 'สร้างรายได้ (Monetization)', en: 'Monetization' }, period: 'Q4 2026', status: 'planned',
    items: [
      { th: 'License & Billing (per-device plans) · Report/PDF · backend แจ้งเตือน', en: 'License & Billing (per-device plans) · Report/PDF · notification backend' },
    ],
  },
  {
    title: { th: 'ขยายสเกล', en: 'Scale' }, period: '2027', status: 'future',
    items: [
      { th: 'ML anomaly · WebSocket streaming · MQTT · mobile/PWA · API docs · tests/CI', en: 'ML anomaly · WebSocket streaming · MQTT · mobile/PWA · API docs · tests/CI' },
    ],
  },
];

/* ────────── 4) สินค้าที่รองรับ (Products) ────────── */
interface Prod { name: string; role: L; conn: string }
interface ProdGroup { title: L; color: string; items: Prod[] }
const PRODUCTS: ProdGroup[] = [
  {
    title: { th: '⚡ Energy Set', en: '⚡ Energy Set' }, color: 'var(--amber)', items: [
      { name: 'Smart Meter 3φ (4G / Wi-Fi)', role: { th: 'Rogowski / Split Core', en: 'Rogowski / Split Core' }, conn: 'MV2 / PG41' },
      { name: 'Smart Meter 1φ DIN Rail', role: { th: 'มิเตอร์ 1 เฟส', en: '1-phase meter' }, conn: 'EM115-WL' },
      { name: 'Smart Meter 1φ + Temp/Humidity', role: { th: 'มิเตอร์ + อุณหภูมิ', en: 'meter + temp' }, conn: 'MV2' },
    ],
  },
  {
    title: { th: '🌡️ Environment / Temp', en: '🌡️ Environment / Temp' }, color: 'var(--green)', items: [
      { name: 'Temp & Humidity Online', role: { th: 'อุณหภูมิ/ความชื้น', en: 'temp/humidity' }, conn: 'ESP8266' },
      { name: 'Temp Data Logger (ห้องเย็น)', role: { th: 'Cold Chain', en: 'cold chain' }, conn: 'ESP8266' },
      { name: 'Wind Speed · Soil (pH/EC/NPK)', role: { th: 'สภาพแวดล้อม/เกษตร', en: 'environment/agri' }, conn: 'ESP8266' },
    ],
  },
  {
    title: { th: '💧 Water / Utility', en: '💧 Water / Utility' }, color: 'var(--cyan)', items: [
      { name: 'Water Level · DO · pH · EC', role: { th: 'คุณภาพ/ระดับน้ำ', en: 'water quality/level' }, conn: 'ESP8266' },
      { name: 'Pressure Sensor', role: { th: 'แรงดันน้ำ/ลม', en: 'water/air pressure' }, conn: 'ESP8266' },
    ],
  },
  {
    title: { th: '📶 Minew Sensor (BLE)', en: '📶 Minew Sensor (BLE)' }, color: 'var(--purple)', items: [
      { name: 'G1-E IoT Gateway', role: { th: 'bridge BLE → MQTT', en: 'BLE bridge → MQTT' }, conn: 'WiFi/LAN/PoE' },
      { name: 'Temp/Humidity BLE (All-in-One / Wired)', role: { th: 'BLE sensor', en: 'BLE sensor' }, conn: 'BLE' },
      { name: 'PT100 Temp (BLE)', role: { th: '−200 ~ +200°C', en: '−200 ~ +200°C' }, conn: 'BLE' },
    ],
  },
  {
    title: { th: '🧠 Core Set (Controllers)', en: '🧠 Core Set (Controllers)' }, color: 'var(--blue)', items: [
      { name: 'MV2 (ESP8266)', role: { th: 'controller WiFi', en: 'WiFi controller' }, conn: 'MQTT' },
      { name: 'MV3 / MV4 (PiZero-H3)', role: { th: 'Mini PLC', en: 'Mini PLC' }, conn: 'HTTP API' },
      { name: 'MV5 (PG41B 4G) · MV6 (ESP32)', role: { th: '4G / Mini PLC', en: '4G / Mini PLC' }, conn: 'MQTT' },
      { name: 'V-BOX E-4G', role: { th: 'gateway 4G', en: '4G gateway' }, conn: 'HTTP API' },
    ],
  },
  {
    title: { th: '🏭 Project / Custom', en: '🏭 Project / Custom' }, color: 'var(--red)', items: [
      { name: 'Core + sensor ตามงาน', role: { th: 'รองรับหลาย solution (เช่น M-Tech)', en: 'multi-solution (e.g. M-Tech)' }, conn: 'MQTT / HTTP' },
    ],
  },
];

/* ────────── 5) Solutions ────────── */
interface Sol { title: L; desc: L; color: string }
const SOLUTIONS: Sol[] = [
  { color: 'var(--blue)', title: { th: 'Energy Monitoring', en: 'Energy Monitoring' }, desc: { th: 'พลังงานโรงงาน/อาคาร/โซลาร์ — ลดค่าไฟ 12–18%', en: 'factory/building/solar energy — cut cost 12–18%' } },
  { color: 'var(--green)', title: { th: 'Environment', en: 'Environment' }, desc: { th: 'อุณหภูมิ/ความชื้น ห้อง server/ห้องเย็น/ยา/แล็บ', en: 'temp/humidity — server room/cold storage/pharma/lab' } },
  { color: 'var(--cyan)', title: { th: 'Water & Utility', en: 'Water & Utility' }, desc: { th: 'ระดับ/แรงดัน/pH-EC/บำบัดน้ำเสีย (WWTP SCADA)', en: 'level/pressure/pH-EC/WWTP (SCADA)' } },
  { color: 'var(--amber)', title: { th: 'Industrial / OEE', en: 'Industrial / OEE' }, desc: { th: 'ประสิทธิภาพเครื่องจักร + predictive maintenance', en: 'machine OEE + predictive maintenance' } },
  { color: 'var(--purple)', title: { th: 'Cold Chain', en: 'Cold Chain' }, desc: { th: 'ตู้เย็นวัคซีน/ยา (ต่อยอดจาก NG Medical)', en: 'vaccine/pharma fridges (via NG Medical)' } },
];

/* ────────── 6) Ecosystem & Integrations ────────── */
interface Eco { name: L; desc: L; status: FStatus }
const ECOSYSTEM: Eco[] = [
  { name: { th: 'Site Manager (white-label)', en: 'Site Manager (white-label)' }, desc: { th: 'subdomain/branding ต่อลูกค้า', en: 'per-customer subdomain/branding' }, status: 'done' },
  { name: { th: 'Mobile App + momoBLE', en: 'Mobile App + momoBLE' }, desc: { th: 'แอปมือถือ + bridge BLE (Minew)', en: 'mobile app + BLE bridge (Minew)' }, status: 'done' },
  { name: { th: 'LINE / Telegram / Email', en: 'LINE / Telegram / Email' }, desc: { th: 'ช่องแจ้งเตือน', en: 'alert channels' }, status: 'doing' },
  { name: { th: 'Momosight (BI)', en: 'Momosight (BI)' }, desc: { th: 'ส่ง metric เข้า dashboard ทีมขาย', en: 'push metrics to sales BI' }, status: 'planned' },
  { name: { th: 'Momo ERP', en: 'Momo ERP' }, desc: { th: 'sales / บิล / คลัง', en: 'sales / billing / inventory' }, status: 'planned' },
  { name: { th: 'Public API / MQTT', en: 'Public API / MQTT' }, desc: { th: 'เชื่อมระบบภายนอก', en: 'external integrations' }, status: 'future' },
];

/* ────────── 7) วิสัยทัศน์ Platform — 5 เสาหลัก ────────── */
interface Pillar { no: string; title: L; desc: L; status: FStatus }
const PILLARS: Pillar[] = [
  { no: '①', title: { th: 'Product / Device Registry', en: 'Product / Device Registry' }, desc: { th: 'template ต่อรุ่นสินค้า — onboard สินค้าไหนก็ได้ (keystone)', en: 'template per product — onboard any device (keystone)' }, status: 'doing' },
  { no: '②', title: { th: 'Multi-protocol Ingestion', en: 'Multi-protocol Ingestion' }, desc: { th: '3 ทาง: MQTT→IoT Processor + HTTP API ×2 (มีจริงแล้ว)', en: '3 paths: MQTT→IoT Processor + HTTP API ×2 (already live)' }, status: 'doing' },
  { no: '③', title: { th: 'Solution Packs', en: 'Solution Packs' }, desc: { th: 'โมดูล vertical เปิด/ปิดต่อลูกค้า', en: 'vertical modules toggled per tenant' }, status: 'doing' },
  { no: '④', title: { th: 'Rules & AI Engine', en: 'Rules & AI Engine' }, desc: { th: 'threshold/anomaly/prediction ต่อ solution', en: 'threshold/anomaly/prediction per solution' }, status: 'done' },
  { no: '⑤', title: { th: 'Ecosystem Glue', en: 'Ecosystem Glue' }, desc: { th: 'billing · ERP · BI · แจ้งเตือน · app · API · white-label', en: 'billing · ERP · BI · notify · app · API · white-label' }, status: 'doing' },
];

/* ────────── icons ────────── */
function ArchIcon({ name, color }: { name: string; color: string }) {
  const s = { width: 18, height: 18 };
  const p = { fill: 'none' as const, stroke: color, strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, style: s };
  switch (name) {
    case 'layout': return <svg viewBox="0 0 24 24" {...p}><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>;
    case 'server': return <svg viewBox="0 0 24 24" {...p}><rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" /><line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" /></svg>;
    case 'database': return <svg viewBox="0 0 24 24" {...p}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>;
    case 'shield': return <svg viewBox="0 0 24 24" {...p}><path d="M12 2l8 4v6c0 5.25-3.5 8.5-8 10-4.5-1.5-8-4.75-8-10V6l8-4z" /><path d="M9 12l2 2 4-4" /></svg>;
    case 'layers': return <svg viewBox="0 0 24 24" {...p}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>;
    case 'data': return <svg viewBox="0 0 24 24" {...p}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>;
    default: return null;
  }
}

function StatusBadge({ status, lang }: { status: FStatus; lang: string }) {
  const m = SM[status];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 99, fontSize: 10.5, fontWeight: 700, color: m.color, background: `color-mix(in srgb, ${m.color} 12%, transparent)`, border: `1px solid color-mix(in srgb, ${m.color} 30%, transparent)`, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.color }} />
      {tx(m.label, lang)}
    </span>
  );
}

function SectionTitle({ color, kicker, title, sub, lang }: { color: string; kicker: L; title: L; sub?: L; lang: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 28, marginBottom: 14 }}>
      <span style={{ width: 4, height: 30, borderRadius: 3, background: color, flexShrink: 0 }} />
      <div>
        <div className="mono-label" style={{ fontSize: 10, color }}>{tx(kicker, lang)}</div>
        <h3 className="display" style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{tx(title, lang)}</h3>
      </div>
      {sub && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-muted)' }}>{tx(sub, lang)}</span>}
    </div>
  );
}

/* ────────── main ────────── */
export default function SystemOverviewPage() {
  const lang = useAppStore((s) => s.lang);

  const stats = useMemo(() => {
    const all = FEATURES.flatMap((g) => g.items);
    const c = { done: 0, doing: 0, planned: 0, future: 0 } as Record<FStatus, number>;
    all.forEach((f) => { c[f.status] += 1; });
    const pct = all.length ? Math.round((c.done / all.length) * 100) : 0;
    return { total: all.length, ...c, pct };
  }, []);

  return (
    <div className="g-panel">
      <div className="g-header">
        <div>
          <h2 className="g-title">{lang === 'th' ? 'ภาพรวมระบบ & แผนพัฒนา' : 'System Overview & Roadmap'}</h2>
          <p className="g-subtitle">{lang === 'th' ? 'โครงสร้าง · ฟีเจอร์ · แผนพัฒนา · สินค้า · solutions · ecosystem · วิสัยทัศน์ ของ MomoSpace 4.0' : 'Structure · features · roadmap · products · solutions · ecosystem · vision of MomoSpace 4.0'}</p>
        </div>
      </div>

      <div className="g-body" style={{ padding: 24 }}>
        {/* Summary stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div style={{ padding: 18, borderRadius: 16, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'var(--gold)', opacity: 0.06 }} />
            <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--gold)', lineHeight: 1 }}>{stats.pct}%</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontWeight: 600 }}>{lang === 'th' ? 'ฟีเจอร์เสร็จแล้ว' : 'Features shipped'}</div>
            <div style={{ marginTop: 8, height: 4, borderRadius: 2, background: 'var(--bg-deep)', overflow: 'hidden' }}>
              <div style={{ width: `${stats.pct}%`, height: '100%', borderRadius: 2, background: 'linear-gradient(90deg, var(--gold), var(--amber))', transition: 'width 1s ease' }} />
            </div>
          </div>
          {([['done', stats.done], ['doing', stats.doing], ['planned', stats.planned], ['future', stats.future]] as [FStatus, number][]).map(([st, n]) => (
            <div key={st} style={{ padding: 18, borderRadius: 16, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: SM[st].color }} />
                <div style={{ fontSize: 26, fontWeight: 800, color: SM[st].color }}>{n}</div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontWeight: 600 }}>{tx(SM[st].label, lang)}</div>
            </div>
          ))}
        </div>

        {/* ── 1) Architecture ── */}
        <SectionTitle color="var(--blue)" lang={lang}
          kicker={{ th: 'ส่วนที่ 1', en: 'PART 1' }}
          title={{ th: 'โครงสร้าง & สถาปัตยกรรม', en: 'Structure & Architecture' }}
          sub={{ th: `${ARCH.length} เลเยอร์`, en: `${ARCH.length} layers` }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {ARCH.map((a, i) => (
            <div key={i} style={{ padding: 16, borderRadius: 14, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: `color-mix(in srgb, ${a.color} 12%, transparent)` }}>
                  <ArchIcon name={a.icon} color={a.color} />
                </span>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{tx(a.title, lang)}</div>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {a.items.map((it, j) => (
                  <li key={j} style={{ display: 'flex', gap: 7, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                    <span style={{ color: a.color, flexShrink: 0, marginTop: 1 }}>›</span>
                    <span>{tx(it, lang)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ── 2) Features ── */}
        <SectionTitle color="var(--purple)" lang={lang}
          kicker={{ th: 'ส่วนที่ 2', en: 'PART 2' }}
          title={{ th: 'ฟีเจอร์ทั้งหมด', en: 'All Features' }}
          sub={{ th: `${stats.total} รายการ`, en: `${stats.total} items` }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {FEATURES.map((g, i) => (
            <div key={i} style={{ padding: 16, borderRadius: 14, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ width: 4, height: 16, borderRadius: 2, background: g.color }} />
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{tx(g.title, lang)}</div>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>{g.items.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {g.items.map((f, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>{tx(f.name, lang)}</span>
                    <StatusBadge status={f.status} lang={lang} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── 3) Roadmap ── */}
        <SectionTitle color="var(--green)" lang={lang}
          kicker={{ th: 'ส่วนที่ 3', en: 'PART 3' }}
          title={{ th: 'แผนพัฒนา (Roadmap)', en: 'Development Roadmap' }}
          sub={{ th: `${PHASES.length} เฟส`, en: `${PHASES.length} phases` }} />
        <div style={{ position: 'relative', paddingLeft: 8 }}>
          {PHASES.map((ph, i) => (
            <div key={i} style={{ display: 'flex', gap: 14, paddingBottom: i === PHASES.length - 1 ? 0 : 14 }}>
              {/* timeline rail */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <span style={{ width: 14, height: 14, borderRadius: '50%', background: `color-mix(in srgb, ${SM[ph.status].color} 20%, transparent)`, border: `2px solid ${SM[ph.status].color}`, flexShrink: 0 }} />
                {i !== PHASES.length - 1 && <span style={{ width: 2, flex: 1, minHeight: 20, background: 'var(--glass-border)', marginTop: 2 }} />}
              </div>
              {/* card */}
              <div style={{ flex: 1, padding: 14, borderRadius: 14, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', marginTop: -2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{tx(ph.title, lang)}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{ph.period}</span>
                  <span style={{ marginLeft: 'auto' }}><StatusBadge status={ph.status} lang={lang} /></span>
                </div>
                {ph.items.map((it, j) => (
                  <p key={j} style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{tx(it, lang)}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── 4) Products ── */}
        <SectionTitle color="var(--cyan)" lang={lang}
          kicker={{ th: 'ส่วนที่ 4', en: 'PART 4' }}
          title={{ th: 'สินค้าที่รองรับ', en: 'Supported Products' }}
          sub={{ th: `${PRODUCTS.reduce((s, g) => s + g.items.length, 0)} รุ่น`, en: `${PRODUCTS.reduce((s, g) => s + g.items.length, 0)} models` }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 12 }}>
          {PRODUCTS.map((g, i) => (
            <div key={i} style={{ padding: 16, borderRadius: 14, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ width: 4, height: 16, borderRadius: 2, background: g.color }} />
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{tx(g.title, lang)}</div>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>{g.items.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {g.items.map((pr, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{pr.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tx(pr.role, lang)}</div>
                    </div>
                    <span style={{ fontSize: 9.5, fontWeight: 700, color: g.color, background: `color-mix(in srgb, ${g.color} 12%, transparent)`, border: `1px solid color-mix(in srgb, ${g.color} 28%, transparent)`, padding: '2px 7px', borderRadius: 99, whiteSpace: 'nowrap' }}>{pr.conn}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p style={{ marginTop: 8, fontSize: 10.5, color: 'var(--text-muted)' }}>
          {lang === 'th' ? '* จัดตาม product set (โหมด A ชุดสำเร็จ · โหมด B project) · แคตตาล็อกเต็ม: docs/product-catalog.md' : '* grouped by product set (A: completed · B: project) · full catalog: docs/product-catalog.md'}
        </p>

        {/* ── 5) Solutions ── */}
        <SectionTitle color="var(--amber)" lang={lang}
          kicker={{ th: 'ส่วนที่ 5', en: 'PART 5' }}
          title={{ th: 'Solutions ที่รองรับ', en: 'Solutions' }}
          sub={{ th: `${SOLUTIONS.length} กลุ่ม`, en: `${SOLUTIONS.length} verticals` }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {SOLUTIONS.map((s, i) => (
            <div key={i} style={{ padding: 16, borderRadius: 14, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', borderLeft: `3px solid ${s.color}` }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{tx(s.title, lang)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{tx(s.desc, lang)}</div>
            </div>
          ))}
        </div>

        {/* ── 6) Ecosystem ── */}
        <SectionTitle color="var(--red)" lang={lang}
          kicker={{ th: 'ส่วนที่ 6', en: 'PART 6' }}
          title={{ th: 'Ecosystem & การเชื่อมต่อ', en: 'Ecosystem & Integrations' }}
          sub={{ th: `${ECOSYSTEM.length} จุดเชื่อม`, en: `${ECOSYSTEM.length} integrations` }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {ECOSYSTEM.map((e, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>{tx(e.name, lang)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tx(e.desc, lang)}</div>
              </div>
              <StatusBadge status={e.status} lang={lang} />
            </div>
          ))}
        </div>

        {/* ── วิสัยทัศน์: 5 เสาหลัก ── */}
        <SectionTitle color="var(--gold)" lang={lang}
          kicker={{ th: 'วิสัยทัศน์', en: 'VISION' }}
          title={{ th: 'Platform & Ecosystem — 5 เสาหลัก', en: 'Platform & Ecosystem — 5 Pillars' }}
          sub={{ th: 'ยกระดับเป็น platform กลาง', en: 'toward a central platform' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 12 }}>
          {PILLARS.map((p, i) => (
            <div key={i} style={{ padding: 16, borderRadius: 14, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--gold)', lineHeight: 1 }}>{p.no}</span>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>{tx(p.title, lang)}</div>
                <StatusBadge status={p.status} lang={lang} />
              </div>
              <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{tx(p.desc, lang)}</p>
            </div>
          ))}
        </div>
        <p style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>
          {lang === 'th' ? '📐 รายละเอียดสถาปัตยกรรมเต็มที่ docs/architecture-blueprint.md' : '📐 Full architecture in docs/architecture-blueprint.md'}
        </p>

        <p style={{ marginTop: 20, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
          {lang === 'th'
            ? 'แก้ข้อมูลได้ที่ components/pages/SystemOverviewPage.tsx (ARCH · FEATURES · PHASES · PRODUCTS · SOLUTIONS · ECOSYSTEM · PILLARS)'
            : 'Edit data in components/pages/SystemOverviewPage.tsx (ARCH · FEATURES · PHASES · PRODUCTS · SOLUTIONS · ECOSYSTEM · PILLARS)'}
        </p>
      </div>
    </div>
  );
}
